import { useCallback, useEffect, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { RenderMetricsCollector, type RenderMetricSample } from '@org/runtime-engine';
import type { ShadcnLayoutType, ShadcnPageType } from '../types';

interface UseRenderMetricsOptions {
  appId?: string;
  pageId?: string;
  pageType: ShadcnPageType;
  flushIntervalMs?: number;
}

const DEFAULT_USAGE_TRACKER_ENDPOINT = '/api/usage-tracker/render-metrics';
const DEFAULT_FLUSH_INTERVAL_MS = 30_000;
const FRAME_BUDGET_MS = 16;

export function useRenderMetrics(layoutType: ShadcnLayoutType | 'flat', options: UseRenderMetricsOptions) {
  const collectorRef = useRef(new RenderMetricsCollector());
  const queueRef = useRef<RenderMetricSample[]>([]);
  const overBudgetStreakRef = useRef<Record<string, number>>({});

  const flushIntervalMs = options.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
  const usageTrackerEndpoint = (import.meta.env.VITE_USAGE_TRACKER_WORKER_URL as string | undefined) ?? DEFAULT_USAGE_TRACKER_ENDPOINT;

  const flush = useCallback(async () => {
    if (queueRef.current.length === 0) {
      return;
    }

    const metrics = queueRef.current;
    queueRef.current = [];

    const summary = collectorRef.current.getSummary();
    collectorRef.current.reset();

    try {
      await fetch(usageTrackerEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId: options.appId,
          pageId: options.pageId,
          source: 'runtime-shell',
          capturedAt: new Date().toISOString(),
          metrics,
          summary,
        }),
      });
    } catch (_error) {
      queueRef.current.unshift(...metrics);
    }
  }, [options.appId, options.pageId, usageTrackerEndpoint]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void flush();
    }, flushIntervalMs);

    return () => {
      window.clearInterval(intervalId);
      void flush();
    };
  }, [flush, flushIntervalMs]);

  return useMemo(
    () =>
      (
        _id: string,
        phase: 'mount' | 'update',
        actualDuration: number,
        baseDuration: number,
      ) => {
        const sample: RenderMetricSample = {
          layoutType,
          pageType: options.pageType,
          phase,
          actualDuration,
          baseDuration,
          timestamp: Date.now(),
        };

        queueRef.current.push(sample);
        collectorRef.current.add(sample);

        const streakKey = `${layoutType}:${options.pageType}`;
        const currentStreak = overBudgetStreakRef.current[streakKey] ?? 0;
        const nextStreak = actualDuration > FRAME_BUDGET_MS ? currentStreak + 1 : 0;
        overBudgetStreakRef.current[streakKey] = nextStreak;

        if (nextStreak === 3) {
          Sentry.captureMessage('Layout render performance regression detected', {
            level: 'warning',
            tags: {
              layoutType,
              pageType: options.pageType,
            },
            extra: {
              appId: options.appId,
              pageId: options.pageId,
              actualDuration,
              baseDuration,
              frameBudgetMs: FRAME_BUDGET_MS,
            },
          });
        }
      },
    [layoutType, options.appId, options.pageId, options.pageType],
  );
}
